import { Injectable, EventEmitter, signal, inject, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval, Subscription } from 'rxjs';
import { ConfirmService } from './confirm.service';
import { StorageService } from './storage';
import { APP_VERSION, CLEANUP_VERSIONS, VERSION_STORAGE_KEY } from '../core/tokens';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  public updateAvailable = new EventEmitter<VersionReadyEvent|boolean>();

  /** Wird auf true gesetzt wenn Speicherbereinigung erforderlich ist */
  public cleanupRequired = signal<boolean>(false);

  private confirmService = inject(ConfirmService);
  private storageService = inject(StorageService);
  private appVersion = inject(APP_VERSION);
  private cleanupVersions = inject(CLEANUP_VERSIONS);
  private versionStorageKey = inject(VERSION_STORAGE_KEY);
  private ngZone = inject(NgZone);

  /** Status für UI: 'idle', 'checking', 'downloading', 'activating' */
  public status = signal<'idle' | 'checking' | 'downloading' | 'activating'>('idle');

  /** True wenn ein Update heruntergeladen wurde aber noch nicht aktiviert ist */
  public updateReady = signal<boolean>(false);

  private checkIntervalMs = 30 * 60 * 1000; // 30 Minuten
  private intervalSub?: Subscription;
  private initialized = false;
  private isChecking = false;

  constructor(private swUpdate: SwUpdate) {
    // Prüfen ob die aktuelle Version eine Speicherbereinigung erfordert
    this.checkVersionCleanup();

    if (this.swUpdate.isEnabled) {
      this.initUpdateChecks();
    } else {
      // SW noch nicht registriert — Retry nach 5 Sekunden
      setTimeout(() => {
        if (this.swUpdate.isEnabled && !this.initialized) {
          console.log('[SW] Späte Registrierung erkannt, starte Update-Checks');
          this.initUpdateChecks();
        }
      }, 5000);
    }

    // Bei Tab-Wechsel (Sichtbarkeit) Update prüfen
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.swUpdate.isEnabled) {
        this.checkForUpdates();
      }
    });
  }

  private initUpdateChecks(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Reagiert auf neue Updates
    this.swUpdate.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case 'VERSION_DETECTED':
          this.status.set('downloading');
          this.updateAvailable.emit(true);
          console.log('[SW] Neue Version erkannt:', evt.version.hash);
          break;
        case 'VERSION_READY':
          this.status.set('idle');
          this.updateReady.set(true);
          console.log('[SW] Neue Version verfügbar', evt);
          this.updateAvailable.emit(evt);
          break;
        case 'VERSION_INSTALLATION_FAILED':
          this.status.set('idle');
          this.updateAvailable.emit(false);
          console.error('[SW] Installation der neuen Version fehlgeschlagen:', evt.error);
          break;
        case 'NO_NEW_VERSION_DETECTED':
          this.status.set('idle');
          // Nicht false emittieren wenn bereits ein Update bereitsteht
          if (!this.updateReady()) {
            this.updateAvailable.emit(false);
          }
          console.log('[SW] Keine neue Version gefunden:', evt.version);
          break;
      }
    });

    // Initiales & regelmäßiges Prüfen starten
    this.checkForUpdates();
    // Interval außerhalb der NgZone starten, damit es nicht Change Detection triggert
    this.ngZone.runOutsideAngular(() => {
      this.intervalSub = interval(this.checkIntervalMs).subscribe(() => {
        this.ngZone.run(() => this.checkForUpdates());
      });
    });
  }

  /**
   * Prüft beim App-Start ob die aktuelle Version eine Speicherbereinigung erfordert.
   * Dies passiert wenn die App auf eine Version aktualisiert wurde, die in
   * den konfigurierten Cleanup-Versionen gelistet ist.
   */
  private async checkVersionCleanup(): Promise<void> {
    const currentVersion = this.appVersion;
    const lastVersion = localStorage.getItem(this.versionStorageKey);

    console.log(`[Update] Version: ${currentVersion}, letzte bekannte Version: ${lastVersion}`);

    // Prüfen ob aktuelle Version Cleanup erfordert UND ob es ein Upgrade ist (nicht erste Installation)
    const requiresCleanup = this.cleanupVersions.includes(currentVersion);
    const isUpgrade = lastVersion !== null && lastVersion !== currentVersion;

    if (requiresCleanup && isUpgrade) {
      console.log('[Update] Version erfordert Speicherbereinigung');
      this.cleanupRequired.set(true);

      const confirmed = await this.confirmService.confirm({
        title: 'Wichtiges Update auf Version ' + currentVersion,
        message: `Diese Version enthält wichtige Änderungen, die eine Bereinigung des lokalen Speichers erfordern.\n\n` +
          `⚠️ ACHTUNG: Alle lokal gespeicherten Daten werden gelöscht!\n\n` +
          `Nach der Bereinigung ist eine Neuanmeldung erforderlich.\n\n` +
          `Bitte stellen Sie sicher, dass:\n` +
          `• Alle Änderungen mit dem Server synchronisiert wurden\n` +
          `• Sie eine stabile Internetverbindung haben\n\n` +
          `💡 Tipp: Ein lokaler Export Ihrer Daten (Menü → Exportieren) dient als zusätzliche Sicherung.\n\n` +
          `Möchten Sie die Bereinigung jetzt durchführen?`,
        confirmText: 'Jetzt bereinigen',
        cancelText: 'Später',
        confirmClass: 'btn-warning'
      });

      if (confirmed) {
        // Speicher bereinigen
        await this.storageService.cleanUpSilent();
        // Version als bekannt markieren
        localStorage.setItem(this.versionStorageKey, currentVersion);
        // Seite neu laden
        window.location.reload();
      } else {
        // Nutzer will später - App schließen / Tab schließen empfehlen
        await this.confirmService.alert(
          'Bitte schließen Sie die App und synchronisieren Sie Ihre Daten, bevor Sie fortfahren.\n\n' +
          'Die Bereinigung wird beim nächsten Start erneut angefragt.',
          'Bereinigung verschoben'
        );
      }
    } else {
      // Version als bekannt markieren (für zukünftige Updates)
      localStorage.setItem(this.versionStorageKey, currentVersion);
      this.cleanupRequired.set(false);
    }
  }

  /** Manuell prüfen, ob ein Update verfügbar ist */
  public checkForUpdates(): void {
    if (this.swUpdate.isEnabled && !this.isChecking) {
      this.isChecking = true;
      this.status.set('checking');
      this.swUpdate.checkForUpdate()
        .then(hasUpdate => {
          this.isChecking = false;
          if (!hasUpdate) this.status.set('idle');
          console.log('[SW] Updateprüfung abgeschlossen:', hasUpdate);
        })
        .catch(err => {
          this.isChecking = false;
          this.status.set('idle');
          console.error('[SW] Fehler bei Updateprüfung:', err);
        });
    }
  }

  /** Führt das Update aus und lädt die Seite neu */
  public activateUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.status.set('activating');
      this.updateReady.set(false);
      this.swUpdate.activateUpdate()
        .then(() => {
          console.log('[SW] Update aktiviert. Seite wird neu geladen...');
          document.location.reload();
        })
        .catch(err => {
          this.status.set('idle');
          console.error('[SW] Fehler beim Aktivieren des Updates:', err);
        });
    }
  }

  /**
   * Aktiviert das Update. Die Warnung bei kritischen Versionen erfolgt
   * automatisch beim nächsten App-Start (siehe checkVersionCleanup).
   */
  public async activateUpdateWithWarning(): Promise<void> {
    this.activateUpdate();
  }

  /** Holt die auf dem Server deployte Version aus version.json (am Service Worker vorbei) */
  async fetchServerVersion(): Promise<string | null> {
    try {
      const res = await fetch('/assets/version.json?ngsw-bypass=true');
      const data = await res.json();
      return data.version ?? null;
    } catch {
      return null;
    }
  }

  /** Optional: Aufräumen (z. B. in App-Exit-Hooks) */
  public stop(): void {
    this.intervalSub?.unsubscribe();
  }
}
